import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from '../../../styles/EducationHub.module.css';
import { useEducationHub } from '../../../contexts/EducationHubContext';
import LoadingSpinner from 'components/common/LoadingSpinner';

const UpdatesSection: React.FC = () => {
  const { updates: initialUpdates, updatesLoading: loading, updateTypes: types, updateCategories: categories } = useEducationHub();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  // Get filtered updates
  const filteredUpdates = initialUpdates.filter(update => {
    if (selectedType && update.type !== selectedType) return false;
    if (selectedCategory && update.category !== selectedCategory) return false;
    return true;
  });


  const handleUpdateClick = (update: any) => {
    setSearchParams({ type: 'update', id: update._id });
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Major': return '🚀';
      case 'Minor': return '✨';
      case 'Patch': return '🔧';
      case 'Hotfix': return '🩹';
      default: return '📦';
    }
  };

  const renderUpdateCard = (update: any) => {
    return (
      <div
        key={update._id}
        className={`${styles.contentCard} ${styles.clickableContentCard}`}
        onClick={() => handleUpdateClick(update)}
      >
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>
            {getTypeIcon(update.type)} {update.title}
          </h3>
          <div className={`${styles.typeBadge} ${styles[`priority${update.priority}`]}`}>
            v{update.version}
          </div>
        </div>
        
        <div style={{ 
          fontSize: '14px', 
          color: '#6b7280', 
          marginBottom: '12px',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span>📦 {update.type}</span>
          <span>📂 {update.category}</span>
          <span>🎯 {update.priority}</span>
        </div>
        
        {/* Show content preview */}
        <div className={styles.cardExcerpt}>
          {update.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
        </div>
        
        {/* Show feature highlights */}
        {update.features && update.features.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#16a34a', marginBottom: '4px' }}>
              ✨ New Features ({update.features.length})
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '16px', 
              fontSize: '12px', 
              color: '#6b7280',
              maxHeight: '60px',
              overflow: 'hidden'
            }}>
              {update.features.slice(0, 2).map((feature: string, idx: number) => (
                <li key={idx}>{feature}</li>
              ))}
              {update.features.length > 2 && (
                <li>...and {update.features.length - 2} more</li>
              )}
            </ul>
          </div>
        )}
        
        {/* Show bug fixes */}
        {update.bugFixes && update.bugFixes.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#dc2626', marginBottom: '4px' }}>
              🐛 Bug Fixes ({update.bugFixes.length})
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '16px', 
              fontSize: '12px', 
              color: '#6b7280',
              maxHeight: '60px',
              overflow: 'hidden'
            }}>
              {update.bugFixes.slice(0, 2).map((fix: string, idx: number) => (
                <li key={idx}>{fix}</li>
              ))}
              {update.bugFixes.length > 2 && (
                <li>...and {update.bugFixes.length - 2} more</li>
              )}
            </ul>
          </div>
        )}
        
        <div className={styles.cardMeta}>
          <div className={styles.cardDate}>
            <span>📅 {formatDate(update.releaseDate)}</span>
            <span>👁️ {update.viewCount}</span>
          </div>
          
          {update.downloadUrl && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>💾 Download</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className={styles.filterContainer}>
        <div className={styles.categoryFilterWrapper}>
          <label className={styles.categoryLabel}>Type:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="">All Types</option>
            {types.map(type => (
              <option key={type} value={type}>{getTypeIcon(type)} {type}</option>
            ))}
          </select>
        </div>

        <div className={styles.categoryFilterWrapper}>
          <label className={styles.categoryLabel}>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterResultCount}>
          {filteredUpdates.length} update{filteredUpdates.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Latest Update Banner */}
      {!selectedType && !selectedCategory && filteredUpdates.length > 0 && (
        <div
          className={`${styles.featuredBanner} ${styles.featuredBannerUpdate}`}
          onClick={() => handleUpdateClick(filteredUpdates[0])}
        >
          <div className={styles.featuredBannerHeader}>
            <span>🚀</span>
            <span>Latest Update</span>
          </div>
          <h3 className={styles.featuredBannerTitle}>
            {filteredUpdates[0].title} - v{filteredUpdates[0].version}
          </h3>
          <div className={styles.featuredBannerMeta}>
            Released {formatDate(filteredUpdates[0].releaseDate)} • {filteredUpdates[0].type} Release
          </div>
        </div>
      )}

      {/* Updates Grid */}
      {filteredUpdates.length > 0 ? (
        <div className={styles.contentGrid}>
          {filteredUpdates.slice(selectedType || selectedCategory ? 0 : 1).map(renderUpdateCard)}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🔄</div>
          <h3 className={styles.emptyTitle}>
            {initialUpdates.length === 0 ? "Product Updates Coming Soon!" : "No Updates Match Your Filters"}
          </h3>
          <p className={styles.emptyDescription}>
            {initialUpdates.length === 0 
              ? "Product updates and release notes will be published here when new versions are available. You'll be the first to know about new features and improvements!" 
              : "No updates match your current filters. Try adjusting your search criteria to find the release notes you're looking for."
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default UpdatesSection;
